import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { UserRole } from '../../../core/constants/keys.constant';
import { CreditBalanceResponse } from '../../../models/auth.model';
import { AuthService } from '../../../services/auth.service';

/**
 * "AI Balance" card pinned to the bottom of the sidebar (student and tutor/counsellor nav),
 * replacing the balance that used to sit in the app-wide header. Reads the same `creditBalance$`
 * stream the header used to — populated on login and refreshed by the credit-balance HTTP
 * interceptor — so it stays in sync without a component-local fetch. Collapses to an icon-only
 * chip on the collapsed icon-rail via `sidebar.component.scss` (mirrors the nav items around it).
 */
@Component({
  selector: 'mindbase-ai-balance-card',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, TranslateModule],
  templateUrl: './ai-balance-card.component.html',
  styleUrl: './ai-balance-card.component.scss',
})
export class AiBalanceCardComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  protected creditBalance: CreditBalanceResponse | null = null;

  constructor(private readonly authService: AuthService) {}

  ngOnInit(): void {
    this.authService.creditBalance$.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      this.creditBalance = res;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** `/tutor/credit-packages/packages` for tutor/counsellor, `/credit-packages/packages` for student. */
  protected get creditPackagesRoute(): string {
    const role = this.authService.currentProfileDetails?.role;
    return role === UserRole.TUTOR || role === UserRole.COUNSELOR
      ? '/tutor/credit-packages/packages'
      : '/credit-packages/packages';
  }
}
